import * as React from 'react'
import { DashboardExportCSVModal } from '../../BuiltInDashboard/ExportCSV/ExportCSVModal'
import { getTimeOptions } from '../../TimePickerNavbar/TimeOption'
import { TimeRange } from '@mulesoft/monitoring-center-components/lib/components/BuiltInDashboards/PreBuilt'
import { AutoRefreshFrequency } from '@mulesoft/monitoring-center-components/lib/components/Toolbar/AutoRefresh/model'
import { DateT } from '@mulesoft/anypoint-components/lib/DatePicker'
import { PopoverMenuItem } from '@mulesoft/monitoring-center-components/lib/components/Menu/MenuItemValue'
import { Urls } from '../../Sidebar/Urls'
import { IframeLoadingState } from '@mulesoft/monitoring-center-components'
import DateType from '@mulesoft/monitoring-center-components/lib/utils/date/DateType'
import Tooltip from '@mulesoft/anypoint-components/lib/Tooltip'
import IconPanel from '@mulesoft/anypoint-icons/lib/Icon'
import visualizerApi from '../../../util/VisualizerApi'
import CustomDashboardIframeContainer from './Iframe/CustomDashboardIframeContainer'
import * as labels from '@mulesoft/monitoring-center-components/lib/utils/labels'

import {
  CustomDashboardsHeader
} from '@mulesoft/monitoring-center-components/lib/components/CustomDashboards/CustomDashboardsHeader/CustomDashboardsHeader'

import './MyCustomDashboard.css'

export type ExternalProps = {
  src: string,
  id: string
}

export type ValueProps = ExternalProps & {
  iframeState: IframeLoadingState,
  isTitanium: boolean
  time: TimeRange
  autoRefresh: {
    checked: boolean,
    frequency?: AutoRefreshFrequency
  }
  starred: boolean
  hasEditRole?: boolean
}

export type EventProps = {
  notifyEvent: (type: string, event: string, detail?: any) => void
  rangeChange: (range: TimeRange) => void
  changeDashboardStarred: (starred: boolean) => void
  onAutoRefreshToggle: (checked: boolean, frequency: AutoRefreshFrequency) => void
}

export type Props = ValueProps & EventProps

export type State = {
  title: string,
  canStar: boolean,
  isOpenEditView: boolean,
  exportCSVModalOpened: boolean,
  exportCSVData: any,
  exportCSVPanel: any,
  isRefreshing: boolean
}

class MyCustomDashboardPage extends React.Component<Props, State> {

  static onDeleteDashboard() {
    window.location.hash = Urls.customDashboardPage
  }

  constructor(props: Props) {
    super(props)

    this.state = {
      title: '',
      canStar: false,
      isOpenEditView: false,
      exportCSVModalOpened: false,
      exportCSVData: {},
      exportCSVPanel: {},
      isRefreshing: false
    }

    this.initialValues = this.initialValues.bind(this)
    this.setStateValues = this.setStateValues.bind(this)
    
    this.onExportCSVModalOpen = this.onExportCSVModalOpen.bind(this)
    this.handleExportCSVModalClose = this.handleExportCSVModalClose.bind(this)
    this.handleExportCSVModalConfirmation = this.handleExportCSVModalConfirmation.bind(this)

    this.stopRefreshing = this.stopRefreshing.bind(this)
    this.onRefresh = this.onRefresh.bind(this)

    this.hideEditorDashboard = this.hideEditorDashboard.bind(this)
    this.editView = this.editView.bind(this)

    this.isTimeLowerThanNow = this.isTimeLowerThanNow.bind(this)
    this.downArrowClick = this.downArrowClick.bind(this)
    this.onZoomOut = this.onZoomOut.bind(this)
    this.upArrowClick = this.upArrowClick.bind(this)
    this.changeTime = this.changeTime.bind(this)
    this.onTimeChange = this.onTimeChange.bind(this)

    this.getMainSelector = this.getMainSelector.bind(this)
    this.getExportCSVModalProps = this.getExportCSVModalProps.bind(this)
    this.getHeaderProps = this.getHeaderProps.bind(this)
  }

  initialValues() {
    const dashboard = visualizerApi.currentDashboard()
    if (dashboard) this.setStateValues(dashboard.title, dashboard.meta)
  }

  // State handlers
  setStateValues(title: string, meta: any) {
    this.setState({title: title, canStar: meta.canStar})
    this.props.changeDashboardStarred(!!meta.isStarred)
  }

  // Listeners
  addListeners() {
    visualizerApi.addListener('dashboard-initialized', this.initialValues)
    visualizerApi.addListener('data-received', this.stopRefreshing)
    visualizerApi.addListener('data-error', this.stopRefreshing)
    visualizerApi.addListener('dashboard-deleted', MyCustomDashboardPage.onDeleteDashboard)
    visualizerApi.addListener('hide-dash-editor', this.hideEditorDashboard)
    visualizerApi.addListener('open-export-modal', this.onExportCSVModalOpen)
  }

  removeListeners() {
    visualizerApi.removeListener('dashboard-initialized', this.initialValues)
    visualizerApi.removeListener('data-received', this.stopRefreshing)
    visualizerApi.removeListener('data-error', this.stopRefreshing)
    visualizerApi.removeListener('dashboard-deleted', MyCustomDashboardPage.onDeleteDashboard)
    visualizerApi.removeListener('hide-dash-editor', this.hideEditorDashboard)
    visualizerApi.removeListener('open-export-modal', this.onExportCSVModalOpen)
  }

  // Export CSV handlers
  onExportCSVModalOpen(payload: any) {
    this.setState({exportCSVModalOpened: true, exportCSVData: payload.detail.data, exportCSVPanel: payload.detail.panel})
  }

  handleExportCSVModalClose() {
    this.setState({exportCSVModalOpened: false, exportCSVData: {}, exportCSVPanel: {}})
  }

  handleExportCSVModalConfirmation = (mode: string, dateTimeFormat: string, excel: boolean) => {
    const {exportCSVData, exportCSVPanel} = this.state

    visualizerApi.export(exportCSVData, exportCSVPanel, mode, dateTimeFormat, excel)
    this.handleExportCSVModalClose()
  }

  // Refresh handlers
  stopRefreshing() {
    this.setState({...this.state, isRefreshing: false})
  }

  onRefresh() {
    if (!this.state.isRefreshing && !this.isTimeLowerThanNow()) {
      visualizerApi.refresh()
      this.setState({...this.state, isRefreshing: true})
    }
  }

  // Dashboard handlers
  hideEditorDashboard() {
    this.setState({isOpenEditView: false})
  }

  editView() {
    if (this.state.isOpenEditView) {
      visualizerApi.hideEditorDashboardPanel()
    } else {
      this.setState({isOpenEditView: true})
      visualizerApi.openEditView('settings')
    }
  }

  // Time handlers
  isTimeLowerThanNow() {
    return new Date(this.props.time.to) < new Date()
  }

  downArrowClick() {
    this.changeTime(visualizerApi.move(-1))
  }

  onZoomOut() {
    this.changeTime(visualizerApi.zoomOut())
  }

  upArrowClick() {
    this.changeTime(visualizerApi.move(1))
  }

  changeTime(range: any) {
    const timePickerValue = DateType.getDateT(range)
    this.props.rangeChange(DateType.formatRange({value: timePickerValue}))
  }

  onTimeChange(date: DateT) {
    this.props.rangeChange(DateType.formatRange({value: date}))
  }

  // Props
  getMainSelector() {
    const editDisabled = !this.props.hasEditRole
    const toolTipContent = editDisabled ? labels.missingDashboardPermissions : 'Edit dashboard configurations'
    return (
      <div className={'title-container'}>
        <div className={'search-container'}>
          <span className={'search-title'}>{this.state.title}</span>
        </div>
        <Tooltip
          testId={'edit-icon-tooltip'}
          offset={[0, 0]}
          className={`header-settings ${editDisabled ? 'disabled' : ''}`}
          content={toolTipContent}
          position={'bottom'}
        >
          <IconPanel className={editDisabled ? 'disabled' : ''} data-test-id={'edit-icon'} name={'edit'} size={22} onClick={this.editView}/>
        </Tooltip>
      </div>
    )
  }

  getExportCSVModalProps() {
    return {
      opened: this.state.exportCSVModalOpened,
      chartType: this.state.exportCSVPanel.type ? this.state.exportCSVPanel.type : 'graph',
      onClose: this.handleExportCSVModalClose,
      onApply: this.handleExportCSVModalConfirmation
    }
  }

  getMoreOptionsPopoverProps(): PopoverMenuItem[] {
    const editDisabled = !this.props.hasEditRole
    return [
      {
        item: {
          key: 'duplicate',
          label: 'Duplicate',
          icon: 'duplicate-small',
        },
        onClick: () => visualizerApi.duplicate(),
        testId: 'more-options-duplicate',
        disabled: editDisabled
      },
      {
        item: {
          key: 'delete',
          label: 'Delete',
          icon: 'trash-small'
        },
        onClick: () => visualizerApi.deleteDashboard(),
        testId: 'more-options-delete',
        disabled: editDisabled
      }
    ]
  }

  getHeaderProps() {
    const {time, isTitanium, autoRefresh, starred, changeDashboardStarred, onAutoRefreshToggle} = this.props
    const timeOptionsArr = getTimeOptions(isTitanium)

    return {
      title: this.getMainSelector(),
      fullscreenRootClassName: 'app anypoint-styles',
      visualizerTimePickerProps: {
        onTimeChange: this.onTimeChange,
        time: time,
        predefinedDates: timeOptionsArr
      },
      starredProps: {
        canStar: this.state.canStar,
        starred: starred,
        changeDashboardStarred: changeDashboardStarred
      },
      moreOptionsPopoverProps: {
        options: this.getMoreOptionsPopoverProps()
      },
      hideAutoRefresh: isTitanium ? undefined : true,
      onAutoRefreshToggle: onAutoRefreshToggle,
      autoRefreshChecked: autoRefresh.checked,
      autoRefreshDisabled: this.isTimeLowerThanNow(),
      autoRefreshFrequency: autoRefresh.frequency,
      onRefresh: this.onRefresh,
      onZoomOut: this.onZoomOut,
      downArrowClick: this.downArrowClick,
      upArrowClick: this.upArrowClick,
      isRefreshing: this.state.isRefreshing,
    }
  }

  // Render handlers
  componentDidMount(): void {
    this.addListeners()
    if (visualizerApi.existsFrameApi()) {
      this.initialValues()
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, prevContext: any): void {
    if (this.props.id !== prevProps.id || this.props.src !== prevProps.src) {
      this.addListeners()
    }
  }

  componentWillUnmount() {
    this.removeListeners()
  }

  render() {
    const {src, iframeState, id} = this.props
    const exportModalProps = this.getExportCSVModalProps()
    const headerProps = this.getHeaderProps()
    const showHeader = iframeState === IframeLoadingState.DISPLAYED || iframeState === IframeLoadingState.LOADED

    return (
      <div className={'container'}>
        {showHeader && <CustomDashboardsHeader {...headerProps}/>}
        <CustomDashboardIframeContainer src={src} id={id}/>
        {<DashboardExportCSVModal {...exportModalProps} />}
      </div>
    )
  }
}

export default MyCustomDashboardPage
